import { Select, Slider, Switch, Divider, Typography, Button, Space, InputNumber } from 'antd';
import {
  WifiOutlined,
  CloudOutlined,
  GlobalOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { usePermissions } from '../../../hooks/usePermissions';
import { isTioProvider, type MapTileProvider, type MapBasemapThemeOverride } from '../../Nowcasting/useNowcastingState';
import { NUMERIC_COLORMAPS } from '../../Nowcasting/numericColormaps';
import type { WidgetSettingsProps } from './types';

const { Text } = Typography;

function get<T>(config: Record<string, unknown>, key: string, fallback: T): T {
  const v = config[key];
  return v !== undefined && v !== null ? (v as T) : fallback;
}

export const RadarMapSettings = ({ config, onChange }: WidgetSettingsProps) => {
  const { isRoot } = usePermissions();

  const mapTileProvider = get<MapTileProvider>(config, 'mapTileProvider', 'radar-l2');
  const radarOpacity = get<number>(config, 'radarOpacity', 0.7);
  const mapThemeOverride = get<MapBasemapThemeOverride>(config, 'mapThemeOverride', null);
  const mapStyle = get<string | null>(config, 'mapStyle', null);
  const showWindArrows = get<boolean>(config, 'showWindArrows', false);
  const showStormTracking = get<boolean>(config, 'showStormTracking', false);
  const showRainPath = get<boolean>(config, 'showRainPath', false);
  const isRainOnlyMode = get<boolean>(config, 'isRainOnlyMode', false);
  const trackLineWidth = get<number>(config, 'trackLineWidth', 3);
  const showCircleOverlay = get<boolean>(config, 'showCircleOverlay', false);
  const showRadarBbox = get<boolean>(config, 'showRadarBbox', false);
  const showRadiusCircles = get<boolean>(config, 'showRadiusCircles', false);
  const radiusCircleCount = get<number>(config, 'radiusCircleCount', 3);
  const radiusCircleDistance = get<number>(config, 'radiusCircleDistance', 5);
  const numericNoiseFloor = get<number>(config, 'numericNoiseFloor', 0);
  const numericColormap = get<string>(config, 'numericColormap', 'marshall-palmer');
  const showZoomIndicator = get<boolean>(config, 'showZoomIndicator', false);
  const showLegend = get<boolean>(config, 'showLegend', false);
  const legendCompact = get<boolean>(config, 'legendCompact', false);

  const isTio = isTioProvider(mapTileProvider);
  const isNumeric = mapTileProvider === 'radar-l2-numeric';

  return (
    <div>
      {/* ── Source ── */}
      <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        Radar Source
      </Text>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
        Choose the data source for radar tiles.
      </Text>

      <Space wrap size={[6, 6]} style={{ marginBottom: 12 }}>
        <Button
          type={mapTileProvider === 'radar-l2' ? 'primary' : 'default'}
          size="small"
          icon={<WifiOutlined rotate={90} />}
          onClick={() => onChange({ mapTileProvider: 'radar-l2' })}
        >
          Radar
        </Button>
        <Button
          type={mapTileProvider === 'tomorrow.io-global' ? 'primary' : 'default'}
          size="small"
          icon={<GlobalOutlined />}
          onClick={() => onChange({ mapTileProvider: 'tomorrow.io-global' })}
        >
          Global
        </Button>
        <Button
          type={mapTileProvider === 'tomorrow.io' ? 'primary' : 'default'}
          size="small"
          icon={<CloudOutlined />}
          onClick={() => onChange({ mapTileProvider: 'tomorrow.io' })}
        >
          Tomorrow.io
        </Button>
        {isRoot && (
          <Button
            type={mapTileProvider === 'radar-l2-numeric' ? 'primary' : 'default'}
            size="small"
            icon={<ExperimentOutlined />}
            onClick={() => onChange({ mapTileProvider: 'radar-l2-numeric' })}
          >
            Numeric
          </Button>
        )}
      </Space>

      {/* Opacity */}
      <div style={{ marginBottom: 4 }}>
        <Text style={{ fontSize: 12 }}>Opacity: {Math.round(radarOpacity * 100)}%</Text>
      </div>
      <Slider
        min={0}
        max={1}
        step={0.1}
        value={radarOpacity}
        onChange={(v) => onChange({ radarOpacity: v })}
        tooltip={{ formatter: (v) => `${Math.round((v || 0) * 100)}%` }}
        style={{ marginBottom: 16 }}
      />

      {/* Numeric radar controls */}
      {isNumeric && (
        <>
          <div style={{ marginBottom: 12 }}>
            <Text style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Colormap</Text>
            <Select
              value={numericColormap}
              onChange={(v) => onChange({ numericColormap: v })}
              size="small"
              style={{ width: '100%' }}
              options={NUMERIC_COLORMAPS.map((c) => ({ value: c.id, label: c.name }))}
            />
          </div>
          <div style={{ marginBottom: 4 }}>
            <Text style={{ fontSize: 12 }}>Noise Floor: {numericNoiseFloor.toFixed(1)} mm/hr</Text>
          </div>
          <Slider
            min={0}
            max={2}
            step={0.1}
            value={numericNoiseFloor}
            onChange={(v) => onChange({ numericNoiseFloor: v })}
            tooltip={{ formatter: (v) => `${(v || 0).toFixed(1)} mm/hr` }}
            style={{ marginBottom: 16 }}
          />
        </>
      )}

      <Divider style={{ margin: '16px 0' }} />

      {/* ── Map Appearance ── */}
      <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        Map Appearance
      </Text>

      <div style={{ marginBottom: 12 }}>
        <Text style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Map Theme</Text>
        <Select<string>
          size="small"
          value={mapThemeOverride ?? 'match'}
          onChange={(v) => onChange({ mapThemeOverride: v === 'match' ? null : v })}
          style={{ width: '100%' }}
          options={[
            { value: 'match', label: 'Same as app' },
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
          ]}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Map Background</Text>
        <Select<string>
          size="small"
          value={mapStyle ?? 'default'}
          onChange={(v) => onChange({ mapStyle: v === 'default' ? null : v })}
          style={{ width: '100%' }}
          options={[
            { value: 'default', label: 'Default' },
            { value: 'satellite', label: 'Satellite' },
            { value: 'terrain', label: 'Terrain' },
          ]}
        />
      </div>

      <ToggleRow
        label="Show zoom level"
        checked={showZoomIndicator}
        onChange={(v) => onChange({ showZoomIndicator: v })}
      />
      <ToggleRow
        label="Show legend"
        checked={showLegend}
        onChange={(v) => onChange({ showLegend: v })}
      />
      {showLegend && (
        <ToggleRow
          label="Compact legend"
          checked={legendCompact}
          onChange={(v) => onChange({ legendCompact: v })}
        />
      )}

      <Divider style={{ margin: '16px 0' }} />

      {/* ── Layers ── */}
      <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        Layers
      </Text>

      <ToggleRow label="Wind arrows" checked={showWindArrows} onChange={(v) => onChange({ showWindArrows: v })} />
      <ToggleRow label="Storm tracking" checked={showStormTracking} onChange={(v) => onChange({ showStormTracking: v })} />
      <ToggleRow label="Rain path" checked={showRainPath} onChange={(v) => onChange({ showRainPath: v })} />
      {isTio && (
        <ToggleRow label="Rain only mode" checked={isRainOnlyMode} onChange={(v) => onChange({ isRainOnlyMode: v })} />
      )}

      <Divider style={{ margin: '16px 0' }} />

      {/* ── Track ── */}
      <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        Track
      </Text>
      <div style={{ marginBottom: 4 }}>
        <Text style={{ fontSize: 12 }}>Track line width: {trackLineWidth}px</Text>
      </div>
      <Slider
        min={1}
        max={10}
        step={1}
        value={trackLineWidth}
        onChange={(v) => onChange({ trackLineWidth: v })}
        style={{ marginBottom: 16 }}
      />

      <Divider style={{ margin: '16px 0' }} />

      {/* ── Overlays ── */}
      <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        Overlays
      </Text>

      <ToggleRow label="Circuit circle (3 km)" checked={showCircleOverlay} onChange={(v) => onChange({ showCircleOverlay: v })} />
      <ToggleRow label="Radar bounding box" checked={showRadarBbox} onChange={(v) => onChange({ showRadarBbox: v })} />

      <Divider style={{ margin: '16px 0' }} />

      {/* ── Radius Circles ── */}
      <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
        Radius Circles
      </Text>

      <ToggleRow label="Show radius circles" checked={showRadiusCircles} onChange={(v) => onChange({ showRadiusCircles: v })} />

      {showRadiusCircles && (
        <div style={{ paddingLeft: 8, marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <Text style={{ fontSize: 12 }}>Number of circles</Text>
            <InputNumber
              size="small"
              min={1}
              max={10}
              value={radiusCircleCount}
              onChange={(v) => v != null && onChange({ radiusCircleCount: v })}
              style={{ width: 60 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text style={{ fontSize: 12 }}>Distance (km)</Text>
            <InputNumber
              size="small"
              min={1}
              max={50}
              value={radiusCircleDistance}
              onChange={(v) => v != null && onChange({ radiusCircleDistance: v })}
              style={{ width: 60 }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 10,
    }}>
      <Text style={{ fontSize: 12 }}>{label}</Text>
      <Switch size="small" checked={checked} onChange={onChange} />
    </div>
  );
}
